// CRITICAL: Questo import DEVE essere il primo per intercettare errori Firebase
// prima che React Error Overlay li mostri
import "./utils/firebaseErrorSuppressor";

// CRITICAL: Inizializza i18n PRIMA di getApp per evitare che la landing page
// config (che include 'navigator' nella detection) venga caricata per prima
// tramite router.js → Plans → landing/i18n/config.
// Senza questo ordine, in ambienti con browser inglese (es. CI) l'app
// renderizza in inglese invece che italiano come fallback.
import './i18n/config';

import "@assets/styles/_index.scss";
import { createRoot } from "react-dom/client";
import "react-toastify/dist/ReactToastify.css";
import getApp from "./getApp";
import { errorThrottle } from "./utils/errorThrottle";
import { installToastFilter } from "./utils/toastFilter";
import { installStaleChunkReload } from "./utils/staleChunkReload";
import { restoreFlowArmOnBoot } from "@pages/welcome/utils/flowArm";
import store from "@redux/store";
import { auth } from "@redux/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { avviaCodaFileSalvata } from "@services/codaFileSalvata";

// CRITICAL: prima del gestore globale piu' sotto. Chi ha la scheda aperta
// durante un rilascio chiede chunk che non esistono piu': il rewrite SPA
// risponde index.html e il browser lancia "Unexpected token '<'". Il gestore
// globale si limiterebbe a registrarlo, lasciando l'utente su una schermata
// che non si apre. Qui si ricarica per prendere l'indice nuovo, al massimo
// due volte, perche' un deploy davvero rotto non diventi un ciclo.
installStaleChunkReload();

// Install toast filter early — suppresses raw "Network Error" toasts and deduplicates
installToastFilter();

// Braccio del percorso d'ingresso (corto/lungo) rimesso su gtag prima del primo
// evento: il giro su Stripe ricarica la pagina e senza questo `purchase`,
// `trial_started` e tutto cio' che segue partirebbero senza braccio.
restoreFlowArmOnBoot();

// I file gia' letti che aspettano «Genera» sopravvivono a una ricarica
// (STU-424): la coda si copia sul browser, sotto l'uid di chi e' entrato, e al
// rientro torna nella card. L'uid arriva da Firebase e non da Redux, dove
// alcuni `setUser` lo rimpiazzano con dati di Firestore senza uid.
avviaCodaFileSalvata({
  store,
  osservaUtente: (callback) =>
    onAuthStateChanged(auth, (utente) => callback(utente ? utente.uid : null)),
});

// Lazy load Sentry dopo il primo render per velocizzare il caricamento iniziale
const initSentry = () => {
  import("@sentry/react").then((Sentry) => {
Sentry.init({
  dsn: "https://6a79df7ae43145ec9cd3ea721ed1b1fe@o4508375241195520.ingest.de.sentry.io/4508375245127760",
  environment: process.env.REACT_APP_ENVIRONMENT || 'unknown',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
      tracesSampleRate: 0.1,
  // Limita trace propagation solo alle API del backend, escludendo Stape/GTM per evitare errori CORS
  tracePropagationTargets: [/^http:\/\/localhost:8080/, /^https:\/\/.*\.studierai\.app\/api/],
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 0.5,
  // Ignora completamente errori da librerie esterne (non loggati nemmeno in console)
  ignoreErrors: [
    // Iubenda CMP/CS errors
    /Cannot read properties of undefined \(reading 'cs'\)/,
    /Cannot read properties of undefined \(reading 'cmp'\)/,
    /iubenda/i,
    /autoblocking/i,
    // Script errors generici cross-origin
    /^Script error\.?$/,
    // ResizeObserver (warning noto, non critico)
    /ResizeObserver loop/,
    // Firestore IDB connection drops: il browser (Safari/iOS in particolare)
    // chiude la connessione IndexedDB sotto carico o cambio tab. Il Firestore
    // SDK riapre automaticamente la connessione, quindi l'errore è benigno.
    /Connection to Indexed Database server lost/i,
    /IndexedDB transaction .* failed/i,
    // Firestore IndexedDB wipe (utente pulisce dati / conflitto multi-tab Safari)
    /Database deleted by request of the user/,
  ],
  denyUrls: [
    // Ignora errori da script esterni
    /iubenda\.com/i,
    /cs\.iubenda\.com/i,
    /cdn\.iubenda\.com/i,
    /stape\.net/i,
    /gtm\.js/i,
  ],
  beforeSend(event, hint) {
    const error = hint.originalException;
    if (error && errorThrottle.shouldFilterError(error)) {
          return null;
    }
    if (error && !errorThrottle.shouldSendError(error)) {
          return null;
    }
    return event;
  }
});
  });
};

// Inizializza Sentry dopo 2 secondi (non blocca il render iniziale)
setTimeout(initSentry, 2000);

// Error handler globale per catturare errori non gestiti
// NOTA: Gli errori Firebase offline sono già gestiti dall'early handler sopra
// CRITICAL: Usa capture phase (true) per eseguire PRIMA di Sentry e altri handler
window.addEventListener('error', function(event) {
  const error = event.error || new Error(event.message);
  const errorMessage = event.message || error.message || '';

  // ✅ Ignora "Script error" generici da script cross-origin (Google Analytics, Iubenda, etc.)
  if (errorMessage === 'Script error.' || errorMessage === 'Script error' || errorMessage === '') {
    event.preventDefault();
    event.stopImmediatePropagation(); // CRITICAL: Ferma propagazione a Sentry/altri handler
    return;
  }

  // ✅ Ignora ResizeObserver errors (warning noto di React/browser, non critico)
  if (errorMessage.includes('ResizeObserver loop')) {
    event.preventDefault();
    event.stopImmediatePropagation(); // CRITICAL: Ferma propagazione
    return;
  }

  // ✅ Ignora errori Firebase offline
  if (errorMessage.includes('app-offline') || errorMessage.includes('Application offline')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // ✅ Ignora errori Iubenda
  if (errorMessage.includes("reading 'cs'") || errorMessage.includes("reading 'cmp'")) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // Usa il nostro sistema di throttling (include filtro per Firebase offline)
  if (!errorThrottle.shouldFilterError(error) && errorThrottle.shouldSendError(error)) {
    console.error('Global error caught:', error);
  }
}, true); // CRITICAL: capture phase per eseguire prima di altri handler

// Handler per promise rejections non gestite
// NOTA: Gli errori Firebase offline sono già gestiti dall'early handler sopra
window.addEventListener('unhandledrejection', function(event) {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  const errorMessage = error?.message || '';

  // ✅ Ignora errori Firebase offline
  if (errorMessage.includes('app-offline') || errorMessage.includes('Application offline')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // ✅ Ignora drop di connessione IndexedDB di Firestore (browser/SDK auto-recovery)
  if (errorMessage.includes('Connection to Indexed Database server lost') ||
      errorMessage.includes('IndexedDB transaction')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // Usa il nostro sistema di throttling (include filtro per Firebase offline)
  if (!errorThrottle.shouldFilterError(error) && errorThrottle.shouldSendError(error)) {
    console.error('Unhandled promise rejection:', error);
  }
}, true); // CRITICAL: capture phase

if(process.env.NODE_ENV !== "test") {
  const root = createRoot(document.getElementById("root"));
  if (process.env.REACT_APP_ENVIRONMENT === "production") {
    // Silenzia i log invasivi solo in produzione mantenendo gli errori
    // (non modifica alcuna logica applicativa)
    console.log = function() {};
    console.debug = function() {};
    console.info = function() {};
    console.warn = function() {};
  }
  root.render(getApp());
};




